import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { IGradeIncosistencia } from '@app/modules/produto/models';
import { convertTimeZoneMinus, getOnlyTimeZone } from '@app/shared/utils';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ConfigService,
  EntityParams,
  EntityService,
  TokenService
} from 'viggo-sistemas-core-web';
import { IEntityPagination } from 'viggo-sistemas-core-web/modules/core/model/entity.model';
import { IMateriaPrimaVariacao } from '../models/materia-prima-variacao.model';
import { IMateriaPrima } from '../models/materia-prima.model';

@Injectable()
export class MateriaPrimaService extends EntityService<IMateriaPrima> {
  constructor(
    protected tokenService: TokenService,
    protected configService: ConfigService
  ) {
    super({
      baseEntityEndPoint: 'materia_prima',
      urlBackend: configService.backendURL
    });
  }

  /**
   * Responsável por gerar o PDF/XLSX do relatório por estoque
   * @returns
   */
  gerarRelatorioPorEstoque(
    type: string,
    params: string | EntityParams<any>,
    domainId: string
  ): Observable<any> {
    var url = `${this.baseURL}`;

    if (type === 'PDF') {
      url += '/relatorio_estoque_pdf';
    } else {
      url += '/relatorio_estoque_xlsx';
    }
    let paramsAux: string = null;
    let paramsHttp = new HttpParams();

    const today = convertTimeZoneMinus(new Date());
    // var dataSolicitacao = convertDateToMaskTimezone(
    //   `${today.getFullYear()}-${today.getUTCMonth() + 1}-${today.getDate()}`
    // );

    if (typeof params === 'string') {
      paramsAux = `?${params}`;
      if (domainId) paramsAux += `&domain_id=${domainId}`;
    } else {
      if (params.where) paramsHttp = this.getParams(params.where);
      if (domainId) paramsHttp = paramsHttp.set('domain_id', domainId);
      if (params.include && params.include.length)
        paramsHttp = paramsHttp.set('include', params.include.join(','));
      if (params.order_by && params.order_by.length)
        paramsHttp = paramsHttp.set('order_by', params.order_by.join(','));
      if (params.list_options)
        paramsHttp = paramsHttp.set('list_options', params.list_options);
      paramsHttp = paramsHttp.set('data_solicitacao', today.toISOString());
      paramsAux = null;
    }

    return this.http.get(url + (paramsAux || ''), {
      headers: this.getHeader(),
      params: paramsHttp,
      responseType: 'blob'
    });
  }

  /**
   * Responsável por gerar o PDF/XLSX do relatório por estoque de depósito
   * @returns
   */
  gerarRelatorioPorEstoqueDeposito(
    type: string,
    params: string | EntityParams<any>,
    domainId: string
  ): Observable<any> {
    var url = `${this.baseURL}`;

    if (type === 'PDF') {
      url += '/relatorio_estoque_deposito_pdf';
    } else {
      url += '/relatorio_estoque_deposito_xlsx';
    }
    let paramsAux: string = null;
    let paramsHttp = new HttpParams();

    const today = convertTimeZoneMinus(new Date());
    // var dataSolicitacao = convertDateToMaskTimezone(
    //   `${today.getFullYear()}-${today.getUTCMonth() + 1}-${today.getDate()}`
    // );

    if (typeof params === 'string') {
      paramsAux = `?${params}`;
      if (domainId) paramsAux += `&domain_id=${domainId}`;
    } else {
      if (params.where) paramsHttp = this.getParams(params.where);
      if (domainId) paramsHttp = paramsHttp.set('domain_id', domainId);
      if (params.include && params.include.length)
        paramsHttp = paramsHttp.set('include', params.include.join(','));
      if (params.order_by && params.order_by.length)
        paramsHttp = paramsHttp.set('order_by', params.order_by.join(','));
      if (params.list_options)
        paramsHttp = paramsHttp.set('list_options', params.list_options);
      paramsHttp = paramsHttp.set('data_solicitacao', today.toISOString());
      paramsAux = null;
    }

    return this.http.get(url + (paramsAux || ''), {
      headers: this.getHeader(),
      params: paramsHttp,
      responseType: 'blob'
    });
  }

  /**
   * Pesquisa uma lista de entidades apartir de um parametro repassado
   * @param params parametros repassados
   * @param domainId identificador do domain a ser buscado
   */
  override getEntitiesByParams(
    params: string | EntityParams<IMateriaPrima>,
    domainId?: string,
    require_pagination = false,
    variacoes = false
  ): Observable<any> {
    // T[] | IEntityPagination<T[]>
    let paramsAux: string = null;
    let paramsHttp = new HttpParams();

    if (typeof params === 'string') {
      paramsAux = `?${params}${
        require_pagination ? '&require_pagination=true' : ''
      }`;
      if (domainId) paramsAux += `&domain_id=${domainId}`;
    } else {
      if (params.where) paramsHttp = this.getParams(params.where);
      if (domainId) paramsHttp = paramsHttp.set('domain_id', domainId);
      if (require_pagination) {
        paramsHttp = paramsHttp.set('require_pagination', require_pagination);
      }
      if (params.pagination) {
        paramsHttp = paramsHttp.set('page', params.pagination.page);
        paramsHttp = paramsHttp.set('page_size', params.pagination.page_size);
        if (params.pagination.require_pagination)
          paramsHttp = paramsHttp.set(
            'require_pagination',
            params.pagination.require_pagination
          );
      }
      if (params.include && params.include.length)
        paramsHttp = paramsHttp.set('include', params.include.join(','));
      if (params.order_by && params.order_by.length)
        paramsHttp = paramsHttp.set('order_by', params.order_by.join(','));
      if (params.list_options)
        paramsHttp = paramsHttp.set('list_options', params.list_options);
      paramsAux = null;
    }

    const url = variacoes
      ? this.baseURL + '/variacoes' + (paramsAux || '')
      : this.baseURL + (paramsAux || '');

    return this.http
      .get<any>(url, {
        headers: this.getHeader(),
        params: paramsHttp
      })
      .pipe(
        map(data => {
          if (
            (typeof params === 'object' &&
              params.pagination &&
              params.pagination.require_pagination) ||
            require_pagination
          ) {
            data = data as IEntityPagination<IMateriaPrima>;
            return {
              data: data[this.entitiesName],
              pagination: data.pagination
            };
          }
          data = data as IMateriaPrima[];
          return data[this.entitiesName];
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Busca uma entidade pelo ID
   * @param id identificador da entidade
   * @param include relacionamentos a serem carregados, use apenas o array de strings
   */
  override getEntity(
    id: string,
    include?: string | string[],
    materia_prima_variacao_id?: string
  ): Observable<IMateriaPrima> {
    const url = `${this.baseURL}/${id}`;
    let params = new HttpParams();
    if (include)
      params = params.set(
        'include',
        Array.isArray(include) ? include.join(',') : include
      );

    if (materia_prima_variacao_id)
      params = params.append(
        'materia_prima_variacao_id',
        materia_prima_variacao_id
      );

    return this.http
      .get<IMateriaPrima>(url, { headers: this.getHeader(), params })
      .pipe(
        map(data => data[this.entityName]),
        catchError(this.handleError)
      );
  }
  /**
   * Envia para a API um JSON das variações selecionadas e a descrição do produto
   * para obter de retorno os produtos variação
   * @param variacoes
   * @param descricao
   * @param produto_id
   */
  public gerarMateriasVariacoes(
    variacoes,
    descricao: string,
    quantidade: number,
    valor: number,
    materia_prima_id?: string
  ): Observable<{
    materia_prima_id: string;
    variacoes: IMateriaPrimaVariacao[];
  }> {
    const url = `${this.baseURL}/analisar_variacoes`;
    return this.http.post<{
      materia_prima_id: string;
      variacoes: IMateriaPrimaVariacao[];
    }>(
      url,
      {
        variacoes: variacoes,
        materia_prima_descricao: descricao,
        // materia_prima_quantidade: quantidade,
        // materia_prima_valor_minimo: valor,
        materia_prima_id: materia_prima_id
      },
      { headers: this.getHeader() }
    );
  }

  /**
   * Zerar estoque do produto
   * @param materia_prima_id
   * @returns
   */
  zerarEstoqueMaterial(materia_prima_id: string): Observable<any> {
    const url = `${this.baseURL}/${materia_prima_id}/zerar_estoque`;
    const headers = this.getHeader();
    return this.http.put<any>(url, null, { headers });
  }

  /**
   * Dado um determinado produto é analisado onde está sendo utilizado
   * e caso o usuário queira solucionar essas inconsistências
   * deve encerrar o ciclo do produto nessas entidades para aplicar
   * a crição de grade
   * @param materia_prima_id
   * @returns
   */
  analisarCriacaoGrade(
    materia_prima_id: string
  ): Observable<IGradeIncosistencia> {
    const url = `${this.baseURL}/${materia_prima_id}/analisar_inconsistencia_grade`;
    const headers = this.getHeader();
    return this.http.get<IGradeIncosistencia>(url, { headers });
  }

  /**
   * Responsável por gerar o KARDEX (ANALITICO - GERAL)
   * @returns
   */
  gerarKardex(type: string, params: any): Observable<any> {
    var url = `${this.baseURL}`;

    if (type === 'ANALITICO') {
      url += `/${params.mp_id}/kardex`;
    } else {
      url += '/kardex';
    }

    const today = convertTimeZoneMinus(new Date());
    params['data_solicitacao'] = today.toISOString();
    params['timezone'] = getOnlyTimeZone(today);
    delete params['mp_id'];
    return this.http.post(url, params, {
      headers: this.getHeader(),
      responseType: 'blob'
    });
  }
}
